import { useEffect, useMemo, useState } from "react";

import { Page } from "../../../components/page-base";
import useUserStore from "../../../store/user";
import { profileQualityService } from "../services";
import type { EstadoDeAcesso, PerfilQualidade, QualidadePorEmail } from "../types";
import { descricaoDoCliente, situacaoDoCliente } from "./cliente";
import { ESTADOS, desdeQuando, somar } from "./estados";
import { alternarEstado, linhasVisiveis, ordemDaTabela } from "./filtros";
import { ContasConectadas } from "../components/contas-conectadas";
import type { ContaConectada } from "../types";

const CARTAO = {
  background: "#fff",
  borderRadius: "12px",
  boxShadow: "0 6px 24px rgba(18, 38, 63, 0.06)",
} as const;

export const ProfileQuality = () => {
  const { user } = useUserStore();
  const token = user?.token ?? "";

  const [linhas, setLinhas] = useState<QualidadePorEmail[]>([]);
  const [perfis, setPerfis] = useState<PerfilQualidade[]>([]);
  const [emailAberto, setEmailAberto] = useState<string | null>(null);
  const [estadoAberto, setEstadoAberto] = useState<EstadoDeAcesso | null>(null);
  const [filtro, setFiltro] = useState<EstadoDeAcesso | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [busca, setBusca] = useState("");
  const [contas, setContas] = useState<ContaConectada[]>([]);
  const [carregandoContas, setCarregandoContas] = useState(true);
  const [verificando, setVerificando] = useState<number | "todas" | null>(null);

  const recarregarContas = async () => {
    setCarregandoContas(true);
    setContas(await profileQualityService.getContas(token));
    setCarregandoContas(false);
  };

  useEffect(() => {
    if (!token) return;
    recarregarContas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const verificarConvites = async (userId: number) => {
    setVerificando(userId);
    await profileQualityService.verificarConvites(token, userId);
    await recarregarContas();
    setVerificando(null);
  };

  const verificarTodas = async () => {
    setVerificando("todas");
    await profileQualityService.verificarTodosOsConvites(token);
    setVerificando(null);
  };

  useEffect(() => {
    if (!token) return;
    setCarregando(true);
    profileQualityService
      .getQualidadePorEmail(token)
      .then(setLinhas)
      .finally(() => setCarregando(false));
  }, [token]);

  const abrir = async (email: string, estado: EstadoDeAcesso) => {
    setEmailAberto(email);
    setEstadoAberto(estado);
    setPerfis(await profileQualityService.getPerfis(token, { email, estado }));
  };

  const fecharDetalhe = () => {
    setEmailAberto(null);
    setPerfis([]);
  };

  const clicarNoCartao = (estado: EstadoDeAcesso) => {
    setFiltro((atual) => alternarEstado(atual, estado));
    fecharDetalhe();
  };

  // Quem opera precisa ver QUAIS perfis estão na situação, não só quantos:
  // com um estado selecionado, a lista completa (todas as contas) vem junto.
  const [listaDoEstado, setListaDoEstado] = useState<PerfilQualidade[]>([]);
  const [carregandoLista, setCarregandoLista] = useState(false);
  useEffect(() => {
    if (!token || !filtro) {
      setListaDoEstado([]);
      return undefined;
    }
    let ativo = true;
    setCarregandoLista(true);
    profileQualityService.getPerfis(token, { estado: filtro }).then((lista) => {
      if (!ativo) return;
      setListaDoEstado(lista);
      setCarregandoLista(false);
    });
    return () => {
      ativo = false;
    };
  }, [token, filtro]);

  const pedirAcesso = async (publicId: string) => {
    if (await profileQualityService.marcarAcessoSolicitado(token, publicId)) {
      setPerfis((atual) => atual.filter((perfil) => perfil.publicId !== publicId));
      setListaDoEstado((atual) => atual.filter((perfil) => perfil.publicId !== publicId));
      setLinhas(await profileQualityService.getQualidadePorEmail(token));
    }
  };

  const visiveis = useMemo(
    () => ordemDaTabela(linhasVisiveis(linhas, filtro, busca), filtro),
    [linhas, filtro, busca],
  );

  const colunas = useMemo(
    () => (filtro ? ESTADOS.filter((estado) => estado.chave === filtro) : ESTADOS),
    [filtro],
  );

  const estadoFiltrado = ESTADOS.find((estado) => estado.chave === filtro);

  return (
    <Page.Root>
      <div style={{ padding: "28px 32px 48px", width: "100%", background: "#f8f9fa" }}>
        <Page.HeaderAux />

        <h1 style={{ fontSize: "26px", fontWeight: 800, letterSpacing: "-0.5px", margin: "8px 0 4px" }}>
          Contas conectadas e qualidade dos perfis
        </h1>
        <p style={{ color: "#5b6675", fontSize: "14px", margin: "0 0 26px" }}>
          Acompanhe cada conta gestora do Google e a saúde do acesso a cada perfil.
        </p>

        <ContasConectadas
          contas={contas}
          carregando={carregandoContas}
          verificando={verificando}
          onVerificar={verificarConvites}
          onVerificarTodas={verificarTodas}
        />

        <h2 style={{ fontSize: "22px", fontWeight: 700, letterSpacing: "-0.4px", margin: "0 0 4px" }}>
          Perfis por situação de acesso
        </h2>
        <p style={{ color: "#5b6675", fontSize: "14px", margin: "0 0 22px" }}>
          Por e-mail que gerencia a conta no Google. Clique em uma situação para ver só as contas que precisam dela.
        </p>

        <div style={{ display: "flex", gap: "12px", flexWrap: "wrap", marginBottom: "20px" }}>
          {ESTADOS.map((estado) => {
            const selecionado = filtro === estado.chave;
            const quantos = somar(linhas, estado.contagem);
            return (
              <button
                key={estado.chave}
                type="button"
                onClick={() => clicarNoCartao(estado.chave)}
                aria-pressed={selecionado}
                title={estado.explicacao}
                style={{
                  ...CARTAO,
                  border: selecionado ? `2px solid ${estado.cor}` : "1px solid #e6eaef",
                  borderLeft: `4px solid ${estado.cor}`,
                  padding: selecionado ? "13px 19px" : "14px 20px",
                  minWidth: "158px",
                  textAlign: "left",
                  cursor: "pointer",
                  font: "inherit",
                  outlineOffset: "2px",
                  transform: selecionado ? "translateY(-1px)" : "none",
                  boxShadow: selecionado
                    ? "0 10px 26px rgba(18, 38, 63, 0.12)"
                    : CARTAO.boxShadow,
                }}
              >
                <div style={{ fontSize: "26px", fontWeight: 800, color: estado.cor, letterSpacing: "-0.8px", lineHeight: 1 }}>
                  {quantos}
                </div>
                <div style={{ marginTop: "7px", fontSize: "13px", fontWeight: 600, color: "#3d4757" }}>
                  {estado.rotulo}
                </div>
              </button>
            );
          })}
        </div>

        {estadoFiltrado && (
          <div style={{ ...CARTAO, marginBottom: "20px", padding: "20px 24px", borderLeft: `4px solid ${estadoFiltrado.cor}` }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: "16px", flexWrap: "wrap" }}>
              <h2 style={{ fontSize: "16px", fontWeight: 700, margin: 0 }}>
                {estadoFiltrado.rotulo}
                <span style={{ color: "#5b6675", fontWeight: 600, marginLeft: "8px" }}>
                  {carregandoLista ? "carregando…" : `${listaDoEstado.length} ${listaDoEstado.length === 1 ? "perfil" : "perfis"}`}
                </span>
              </h2>
              <span style={{ color: "#5b6675", fontSize: "13px" }}>{estadoFiltrado.explicacao}</span>
            </div>
            {!carregandoLista && listaDoEstado.length === 0 && (
              <p style={{ color: "#5b6675", fontSize: "14px", margin: "12px 0 0" }}>Nenhum perfil nesta situação.</p>
            )}
            <ul style={{ listStyle: "none", padding: 0, margin: "12px 0 0", maxHeight: "520px", overflowY: "auto" }}>
              {listaDoEstado.map((perfil) => (
                <li
                  key={perfil.publicId}
                  style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "12px", padding: "10px 0", borderTop: "1px solid #f2f5f8" }}
                >
                  <span style={{ fontSize: "14px", minWidth: 0, display: "flex", flexDirection: "column", gap: "2px" }}>
                    <span>
                      <a href={`/dashboard/${perfil.publicId}`} target="_blank" rel="noopener noreferrer" style={{ fontWeight: 600 }}>
                        {perfil.name}
                      </a>
                      <span style={{ color: "#5b6675" }}>
                        {perfil.city ? ` — ${perfil.city}/${perfil.state ?? ""}` : ""}
                        {` · conferido ${desdeQuando(perfil.accessCheckedAt)}`}
                      </span>
                    </span>
                    <span style={{ color: "#5b6675", fontSize: "12px" }}>
                      {perfil.email ? `Gestor: ${perfil.email}` : "Sem e-mail gestor"}
                      {descricaoDoCliente(perfil) ? ` · Cliente: ${descricaoDoCliente(perfil)}` : ""}
                      {situacaoDoCliente(perfil.customerStatus) ? ` · ${situacaoDoCliente(perfil.customerStatus)}` : ""}
                    </span>
                  </span>
                  {filtro === "SEM_ACESSO" && (
                    <button
                      type="button"
                      onClick={() => pedirAcesso(perfil.publicId)}
                      style={{ minHeight: "34px", padding: "0 12px", borderRadius: "52px", border: "1px solid #d9e0e8", background: "#fff", color: "#1d4b9e", fontSize: "13px", fontWeight: 600, cursor: "pointer", font: "inherit", whiteSpace: "nowrap" }}
                    >
                      Marcar acesso solicitado
                    </button>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}

        <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap", marginBottom: "16px" }}>
          <input
            value={busca}
            onChange={(evento) => setBusca(evento.target.value)}
            placeholder="Filtrar por e-mail"
            style={{
              flexGrow: 1,
              maxWidth: "420px",
              minHeight: "42px",
              padding: "0 14px",
              border: "1px solid #d9e0e8",
              borderRadius: "8px",
              font: "inherit",
              fontSize: "14px",
              background: "#fff",
            }}
          />
          {estadoFiltrado && (
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "8px",
                  padding: "7px 14px",
                  borderRadius: "52px",
                  background: "#eef4fe",
                  color: "#1d4b9e",
                  fontSize: "13px",
                  fontWeight: 700,
                }}
              >
                Mostrando: {estadoFiltrado.rotulo}
                <span style={{ color: "#5b6675", fontWeight: 600 }}>
                  {visiveis.length} {visiveis.length === 1 ? "conta" : "contas"}
                </span>
              </span>
              <button
                type="button"
                onClick={() => {
                  setFiltro(null);
                  fecharDetalhe();
                }}
                style={{
                  minHeight: "36px",
                  padding: "0 14px",
                  borderRadius: "52px",
                  border: "1px solid #d9e0e8",
                  background: "#fff",
                  color: "#3d4757",
                  fontSize: "13px",
                  fontWeight: 600,
                  cursor: "pointer",
                  font: "inherit",
                }}
              >
                Limpar filtro
              </button>
            </div>
          )}
        </div>

        {carregando ? (
          <p style={{ color: "#5b6675" }}>Carregando...</p>
        ) : visiveis.length === 0 ? (
          <div style={{ ...CARTAO, padding: "28px", color: "#5b6675", fontSize: "14px" }}>
            Nenhuma conta nesta situação.
          </div>
        ) : (
          <div style={{ ...CARTAO, overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: filtro ? "540px" : "880px" }}>
              <thead>
                <tr style={{ textAlign: "left", color: "#5b6675", fontSize: "12px", fontWeight: 700 }}>
                  <th style={{ padding: "14px 16px", borderBottom: "1px solid #eef1f5" }}>E-mail gestor</th>
                  <th style={{ padding: "14px 16px", borderBottom: "1px solid #eef1f5" }}>Perfis</th>
                  {colunas.map((estado) => (
                    <th
                      key={estado.chave}
                      title={estado.explicacao}
                      style={{ padding: "14px 16px", borderBottom: "1px solid #eef1f5" }}
                    >
                      {estado.rotulo}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visiveis.map((linha) => (
                  <tr key={linha.email} style={{ borderTop: "1px solid #f2f5f8" }}>
                    <td style={{ padding: "13px 16px", fontWeight: 600, fontSize: "14px" }}>{linha.email}</td>
                    <td style={{ padding: "13px 16px", color: "#5b6675", fontSize: "14px" }}>{linha.total}</td>
                    {colunas.map((estado) => {
                      const quantos = estado.contagem(linha);
                      return (
                        <td key={estado.chave} style={{ padding: "13px 16px" }}>
                          {quantos > 0 ? (
                            <button
                              type="button"
                              onClick={() => abrir(linha.email, estado.chave)}
                              style={{
                                minHeight: "32px",
                                padding: "0 10px",
                                borderRadius: "8px",
                                background: "none",
                                border: "none",
                                cursor: "pointer",
                                color: estado.cor,
                                fontWeight: 700,
                                fontSize: "15px",
                                font: "inherit",
                              }}
                            >
                              {quantos}
                            </button>
                          ) : (
                            <span style={{ color: "#cbd3dc", paddingLeft: "10px" }}>—</span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {emailAberto && estadoAberto && (
          <div style={{ ...CARTAO, marginTop: "20px", padding: "20px 24px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "16px" }}>
              <div>
                <h2 style={{ fontSize: "16px", fontWeight: 700, margin: 0 }}>
                  {ESTADOS.find((estado) => estado.chave === estadoAberto)?.rotulo}
                </h2>
                <div style={{ marginTop: "3px", color: "#5b6675", fontSize: "13px" }}>{emailAberto}</div>
              </div>
              <button
                type="button"
                onClick={fecharDetalhe}
                aria-label="Fechar lista de perfis"
                style={{
                  width: "36px",
                  height: "36px",
                  borderRadius: "8px",
                  border: "1px solid #e6eaef",
                  background: "#fff",
                  cursor: "pointer",
                  fontSize: "16px",
                  color: "#5b6675",
                }}
              >
                ×
              </button>
            </div>
            <ul style={{ listStyle: "none", padding: 0, margin: "14px 0 0" }}>
              {perfis.map((perfil) => (
                <li
                  key={perfil.publicId}
                  style={{
                    borderTop: "1px solid #f2f5f8",
                    padding: "12px 0",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: "16px",
                    flexWrap: "wrap",
                  }}
                >
                  <span style={{ fontSize: "14px", minWidth: 0 }}>
                    <a
                      href={`/dashboard/${perfil.publicId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ fontWeight: 600, color: "#1a2230" }}
                    >
                      {perfil.name}
                    </a>
                    <span style={{ color: "#5b6675" }}>
                      {perfil.city ? ` — ${perfil.city}/${perfil.state ?? ""}` : ""}
                      {` · conferido ${desdeQuando(perfil.accessCheckedAt)}`}
                    </span>
                    {(descricaoDoCliente(perfil) || situacaoDoCliente(perfil.customerStatus)) && (
                      <span
                        style={{
                          display: "block",
                          marginTop: "3px",
                          color: "#5b6675",
                          fontSize: "13px",
                        }}
                      >
                        {descricaoDoCliente(perfil) && (
                          <>
                            Cliente: <strong style={{ fontWeight: 600 }}>{descricaoDoCliente(perfil)}</strong>
                          </>
                        )}
                        {situacaoDoCliente(perfil.customerStatus) && (
                          <span
                            style={{
                              marginLeft: descricaoDoCliente(perfil) ? "8px" : 0,
                              padding: "2px 9px",
                              borderRadius: "52px",
                              background: "#fdf2e4",
                              color: "#8a5a12",
                              fontSize: "12px",
                              fontWeight: 700,
                            }}
                          >
                            {situacaoDoCliente(perfil.customerStatus)}
                          </span>
                        )}
                      </span>
                    )}
                  </span>
                  {estadoAberto === "SEM_ACESSO" && (
                    <button
                      type="button"
                      onClick={() => pedirAcesso(perfil.publicId)}
                      style={{
                        minHeight: "34px",
                        border: "1px solid #b7791f",
                        color: "#8a5a12",
                        background: "#fff",
                        borderRadius: "52px",
                        padding: "0 14px",
                        cursor: "pointer",
                        fontSize: "13px",
                        fontWeight: 600,
                        font: "inherit",
                      }}
                    >
                      Marcar acesso solicitado
                    </button>
                  )}
                </li>
              ))}
              {perfis.length === 0 && (
                <li style={{ padding: "12px 0", color: "#5b6675", fontSize: "14px" }}>
                  Nenhum perfil nesta situação.
                </li>
              )}
            </ul>
          </div>
        )}
      </div>
    </Page.Root>
  );
};
